/**
 * Today (13-09): "My work today", part 1. Built to the approved mock (design/mock-13, Today tab;
 * D-13-02-1).
 *
 *   - The briefing: the greeting, "4 things for you, 1 overdue", one calm line about my patch.
 *   - The queue: `lensFor(actor).myQueue` + `handedBack` (Oversight, ISS-186) + `myVerifications`
 *     (Oversight), overdue first. Accept / Done (with the inline proof step on `evidence` rows) /
 *     Not mine / Reassign: tap → tick → the card leaves → 6 s undo → the outbox commits.
 *   - 13-10: Decide on ordinary Decisions (recommendation first, one-tap Agree, undo); the
 *     regulatory panel for regulatory calls (Oversight only, nothing pre-selected, a required
 *     reason, no undo: the confirm is the deliberate step); Verify / Sampled OK / Reject (E3) on
 *     Done cards in Oversight's lens.
 *   - "You're clear for today" when the queue is empty, with a link to Done today.
 *   - The rail: Done today (with Reopen, an audited verb) and In others' hands.
 *   - 13-29: "Records waiting on you" under the queue (`useRecordsWaiting`: one list read per register the lens
 *     touches, grouped by `recordQueue`), and the briefing's records line after the count line.
 *
 * Writes go through the shell's outbox, which the PRACTICE writer commits in dev: it logs exactly
 * what would be sent and saves nothing. Live writes are switched on only at 13-13.
 *
 * `TodayPage` wires the runtime (Graph reads, the outbox, the actor) into `TodayBoard`, which takes
 * a `TodaySource` so design/today-preview can render it with synthetic rows. In a dev build,
 * `?asRole=CareManager` (or HRWHS / Finance / Oversight) views the page through another lens.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useReducedMotion } from 'motion/react';
import { useActionRuntime } from '../../hooks/useActionRuntime';
import { useActor } from '../../hooks/useActor';
import { usePublishNavCount } from '../../hooks/useNavCounts';
import { useRegisterMetrics } from '../../hooks/useRegisterMetrics';
import { useActionQueue } from '../../hooks/useActionQueue';
import { liveRecordsSource, useRecordsWaiting } from '../../hooks/useRecordsWaiting';
import type { RecordsWaitingSource } from '../../hooks/useRecordsWaiting';
import type { TodaySource } from '../../hooks/useActionQueue';
import { getIncidentContext, listActionRows, listVerificationRows, loadVerifyContext } from '../../services/actionService';
import { lensFor } from '../../lib/actionAuthority';
import type { PatchMetrics } from '../../lib/briefing';
import { OFFICE_ROLES } from '../../types/auth';
import type { AppRole } from '../../types/auth';
import type { Actor } from '../../types/actionRegister';
import Briefing from '../../components/today/Briefing';
import Queue from '../../components/today/Queue';
import AllClear from '../../components/today/AllClear';
import DoneToday from '../../components/today/DoneToday';
import RecordsWaiting from '../../components/today/RecordsWaiting';
import RegulatoryPanel from '../../components/today/RegulatoryPanel';
import type { RegulatoryDecision } from '../../components/today/RegulatoryPanel';
import { runRefusalText } from '../../lib/todayQueue';
import { optionText } from '../../lib/options';
import type { ActionRow } from '../../types/actionRegister';
import styles from '../../components/today/today.module.css';
import page from './TodayPage.module.css';

// ---------------------------------------------------------------------------
// The app wiring
// ---------------------------------------------------------------------------

/** Dev only: `?asRole=CareManager` shows the page through that lens. Ignored in a production build. */
function devRole(params: URLSearchParams): AppRole | null {
  if (!import.meta.env.DEV) return null;
  const r = params.get('asRole');
  return r && (OFFICE_ROLES as readonly string[]).includes(r) ? (r as AppRole) : null;
}

export default function TodayPage() {
  const { io, outbox, mode, siteError, eTagFor } = useActionRuntime();
  const actorState = useActor();
  const [params] = useSearchParams();
  const asRole = devRole(params);
  const signedIn = actorState.status === 'ready' ? actorState.actor : null;
  const actor = useMemo<Actor | null>(
    () => (signedIn && asRole ? { ...signedIn, roles: [asRole] } : signedIn),
    [signedIn, asRole],
  );
  const { metrics } = useRegisterMetrics();

  const source = useMemo<TodaySource | null>(() => {
    if (!io || !outbox) return null;
    return {
      mode,
      outbox,
      eTagFor,
      listRows: () => listActionRows(io),
      loadContext: async (row) => {
        const ctx = await loadVerifyContext(io, row);
        return { completedByUpn: ctx.completedByUpn, priorRejections: ctx.priorRejections };
      },
      countPendingVerifications: async () => (await listVerificationRows(io, { pendingOnly: true })).length,
      loadIncident: (parentId) => getIncidentContext(io, parentId),
    };
  }, [io, outbox, mode, eTagFor]);

  const register = actor ? lensFor(actor).myPatchRegister : null;
  const patch = useMemo<PatchMetrics | null>(() => {
    if (register === 'incidents' || register === 'complaints') {
      const m = metrics[register];
      return { register, open: m.open, overdue: m.overdue, ...(register === 'incidents' ? { reportable: m.openReportable ?? 0 } : {}) };
    }
    return null;
  }, [register, metrics]);

  // 13-29: the register records waiting on the person. In practice mode their own practice records join the live
  // read (practice refs only, so the two never collide).
  const practiceUpn = mode === 'practice' ? (actor?.upn ?? null) : null;
  const records = useMemo<RecordsWaitingSource | null>(
    () => (io ? liveRecordsSource(io, { practiceUpn }) : null),
    [io, practiceUpn],
  );

  return <TodayBoard source={source} actor={actor} problem={siteError} patch={patch} records={records} />;
}

// ---------------------------------------------------------------------------
// The board (the page itself; the preview renders it with synthetic data)
// ---------------------------------------------------------------------------

export interface TodayBoardProps {
  source: TodaySource | null;
  actor: Actor | null;
  /** Why the runtime could not start (e.g. the Q&S site id failed): said, not hidden. */
  problem?: string | null;
  /** The patch line's register counts (CareManager / HRWHS / Finance). Oversight's comes from the rows. */
  patch?: PatchMetrics | null;
  /** The clock (the preview may pin it). */
  now?: () => number;
  /** 13-29: where "Records waiting on you" reads from (the app's list reads, or the preview's synthetic rows). */
  records?: RecordsWaitingSource | null;
}

export function TodayBoard({ source, actor, problem = null, patch = null, now = Date.now, records = null }: TodayBoardProps) {
  const reducedMotion = useReducedMotion() ?? false;
  const queue = useActionQueue(actor, source, { reducedMotion, now });
  const waiting = useRecordsWaiting(actor, records, { now });
  const doneHeading = useRef<HTMLHeadingElement | null>(null);

  // A clock for the labels, refreshed each minute (the view itself is pinned to the last read).
  const [clock, setClock] = useState(() => now());
  useEffect(() => {
    const t = setInterval(() => setClock(now()), 60_000);
    return () => clearInterval(t);
  }, [now]);

  const oversight = !!actor?.roles.includes('Oversight');
  const ready = queue.status === 'ready';
  const cards = queue.view.queue;
  usePublishNavCount('/', ready ? cards.length : undefined);

  const patchMetrics = useMemo<PatchMetrics | null>(() => {
    if (!actor || !ready) return null;
    if (oversight) {
      const lens = lensFor(actor);
      return { register: 'actions', waiting: queue.rows.filter(lens.releaseQueue).length, toVerify: queue.pendingVerifications };
    }
    return patch;
  }, [actor, ready, oversight, queue.rows, queue.pendingVerifications, patch]);

  // The regulatory panel: one per page, keyed per opening so nothing carries over (13-10).
  const [panel, setPanel] = useState<{ row: ActionRow; readOnly: boolean; n: number; open: boolean } | null>(null);
  const openings = useRef(0);
  const openRegulatory = useCallback((row: ActionRow, readOnly: boolean) => {
    openings.current += 1;
    setPanel({ row, readOnly, n: openings.current, open: true });
  }, []);
  // Closing keeps the row mounted, so React Aria hands focus back to the button that opened it.
  const closePanel = useCallback(() => setPanel((p) => (p ? { ...p, open: false } : p)), []);
  const run = queue.run;
  const decideRegulatory = useCallback(
    (d: RegulatoryDecision): string | null => {
      if (!panel || !panel.open || panel.readOnly) return 'Read only.';
      const result = run(
        'decide',
        panel.row,
        { option: d.option, reason: d.reason, regulatory: true },
        {
          title: `Recorded: ${optionText(d.label)}`,
          description: d.disagreement ? `Reason saved with your name. ${d.disagreement}` : 'Reason saved with your name.',
        },
        { regulatory: true },
        { immediate: true },
      );
      if (!result.ok) return result.reason === 'busy' ? runRefusalText('busy') : runRefusalText(result.reason);
      closePanel();
      return null;
    },
    [panel, run, closePanel],
  );

  const handlers = useMemo(
    () => ({ run: queue.run, reject: queue.reject, openRegulatory, retry: queue.retry, discard: queue.discard }),
    [queue.run, queue.reject, openRegulatory, queue.retry, queue.discard],
  );

  const fresh = useMemo(() => new Set(queue.session.map((s) => s.id)), [queue.session]);
  const showDone = useCallback(() => {
    doneHeading.current?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
    doneHeading.current?.focus({ preventScroll: true });
  }, [reducedMotion]);

  let body;
  if (!source || !actor) {
    body = problem ? (
      <div className={styles.problem} role="alert">
        <p>The Q&amp;S site couldn&rsquo;t be reached, so your queue can&rsquo;t load: {problem}</p>
        <p>Reload the page to try again.</p>
      </div>
    ) : (
      <p className={styles.placeholder}>Getting your queue ready&hellip;</p>
    );
  } else if (queue.status === 'loading') {
    body = <p className={styles.placeholder}>Reading your queue&hellip;</p>;
  } else if (queue.status === 'error') {
    body = (
      <div className={styles.problem} role="alert">
        <p>Your queue couldn&rsquo;t be read: {queue.error}</p>
        <p>
          <button type="button" className={`${styles.btn} ${styles.line} ${styles.sm}`} onClick={queue.reload}>
            Try again
          </button>
        </p>
      </div>
    );
  } else if (cards.length === 0) {
    body = <AllClear takeFocus={queue.session.length > 0} onShowDone={showDone} />;
  } else {
    body = (
      <Queue
        cards={cards}
        actor={actor}
        now={clock}
        ticking={queue.ticking}
        notes={queue.notes}
        morphed={queue.morphed}
        handlers={handlers}
      />
    );
  }

  return (
    <div className={page.frame}>
      <div className={page.page}>
        <section aria-labelledby="greet">
          <Briefing
            actor={actor}
            now={clock}
            queue={ready ? cards.map((c) => c.row) : null}
            patch={patchMetrics}
            records={waiting.line}
          />
          <div className={page.grid}>
            <div>
              <h2 className={page.srOnly}>Your queue</h2>
              {queue.error && ready && (
                <p className={styles.note} role="status" style={{ marginBottom: 12 }}>
                  The last refresh didn&rsquo;t load ({queue.error}). Showing what was read before.
                </p>
              )}
              <div className={styles.sheet}>{body}</div>
              <RecordsWaiting state={waiting} />
            </div>
            <DoneToday
              ref={doneHeading}
              done={queue.view.done}
              others={queue.view.others}
              notes={queue.doneNotes}
              onReopen={(item) => void queue.reopen(item)}
              onViewContext={(row) => openRegulatory(row, !oversight)}
              fresh={fresh}
            />
          </div>
        </section>
      </div>
      <RegulatoryPanel
        opening={panel?.n ?? 0}
        row={panel?.row ?? null}
        isOpen={!!panel?.open}
        readOnly={panel?.readOnly ?? true}
        now={clock}
        loadIncident={source?.loadIncident}
        onClose={closePanel}
        onDecide={decideRegulatory}
      />
    </div>
  );
}
